import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { 
  ChevronLeft, 
  Clock, 
  Users, 
  Monitor, 
  Award, 
  Calendar,
  CheckCircle,
  Play,
  Download,
  Star,
  X,
  Sparkles,
  Beaker
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ScheduleAndFAQ } from "./schedule/ScheduleAndFAQ";
import { scheduleData } from "../data/breeders";

interface CursoAvanzadoPageProps {
  onNavigateBack: () => void;
  onNavigateToCheckout: (courseId: string) => void;
  onNavigateToNuestrosCursos: () => void;
}

export function CursoAvanzadoPage({ onNavigateBack, onNavigateToCheckout, onNavigateToNuestrosCursos }: CursoAvanzadoPageProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showBundleOffer, setShowBundleOffer] = useState(true);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-black text-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-orange-500/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-white hover:text-orange-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-8 h-8 mr-2"
              />
              <span className="text-white text-lg">RebelGenomics</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => onNavigateToCheckout('online-avanzado')}
            >
                {t("courses.advanced.enrollButton")}
            </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="/assets/nodal.png"
            alt="Advanced Laboratory Media Preparation"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 via-black/60 to-black/80"></div>
        </motion.div>

        <div className="relative z-10 container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="bg-red-600 text-white mb-6 text-sm px-4 py-2">
                {t("courses.advanced.badge")}
              </Badge>
              
              <h1 className="text-5xl md:text-7xl mb-6 text-red-500">
                {t("courses.advanced.heroTitle")}
              </h1>

              <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
                {t("courses.advanced.heroDescription")}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Clock className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.advanced.duration")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Users className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.advanced.students")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Monitor className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.advanced.format")}</div>

                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <Award className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <div className="text-lg">{t("courses.advanced.certificate")}</div>

                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white text-xl px-12 py-6 rounded-full"
                  onClick={() => onNavigateToCheckout('online-avanzado')}
                >
                  {t("courses.advanced.enrollButton")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-black hover:bg-white hover:text-black text-xl px-12 py-6 rounded-full"
                >
                  {t("courses.advanced.viewProgram")}
                </Button>
              </div>
            </motion.div>

            {/* Video Preview */}
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <Card className="bg-white/10 backdrop-blur-sm border-red-500/30 p-6 rounded-3xl">
                <div className="relative">
                  <ImageWithFallback
                    src="/assets/nodal.png"
                    alt="Advanced Biotechnology Equipment"
                    className="w-full h-64 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-black/30 rounded-2xl flex items-center justify-center">
                    <Button
                      size="lg"
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border-2 border-white/50 rounded-full p-6"
                    >
                      <Play className="w-8 h-8 text-white" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-xl mb-2 text-red-400">{t("courses.advanced.limitedGroup")}</h3>
                  <p className="text-gray-400">{t("courses.advanced.nextEdition")}</p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - What you'll learn */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="text-4xl md:text-5xl mb-8 text-red-500">
                {t("courses.advanced.whatYouLearn")}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {[
                  t("courses.advanced.bullet1"),
                  t("courses.advanced.bullet2"),
                  t("courses.advanced.bullet3"),
                  t("courses.advanced.bullet4"),
                  t("courses.advanced.bullet5"),
                  t("courses.advanced.bullet6"),
                  t("courses.advanced.bullet7"),
                  t("courses.advanced.bullet8")
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              {/* Course Schedule */}
              <h3 className="text-3xl mb-6 text-white">{t("courses.advanced.schedule")}</h3>
              <div className="space-y-4">
                {[
                  {
                    module: "Módulo 1",
                    title: t("modules.advanced.module1.title"),
                    duration: t("modules.advanced.module1.duration"),
                    topics: t("modules.advanced.module1.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 2", 
                    title: t("modules.advanced.module2.title"),
                    duration: t("modules.advanced.module2.duration"),
                    topics: t("modules.advanced.module2.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 3",
                    title: t("modules.advanced.module3.title"),
                    duration: t("modules.advanced.module3.duration"), 
                    topics: t("modules.advanced.module3.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 4",
                    title: t("modules.advanced.module4.title"),
                    duration: t("modules.advanced.module4.duration"),
                    topics: t("modules.advanced.module4.topics", { returnObjects: true })
                  },
                  {
                    module: "Módulo 5",
                    title: t("modules.advanced.module5.title"),
                    duration: t("modules.advanced.module5.duration"),
                    topics: t("modules.advanced.module5.topics", { returnObjects: true })
                  }
                ].map((module, index) => (
                  <Card key={index} className="bg-white/5 border-red-500/20 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge className="bg-red-600 text-white mb-2">{module.module}</Badge>
                        <h4 className="text-xl text-white">{module.title}</h4>
                      </div>
                      <span className="text-red-400">{module.duration}</span>
                    </div>
                    <ul className="space-y-1">
                      {(module.topics as string[]).map((topic, topicIndex) => (
                        <li key={topicIndex} className="text-gray-400 text-sm">• {topic}</li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Course Info & Registration */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white/10 backdrop-blur-sm border-red-500/30 p-8 rounded-3xl sticky top-24">
                <div className="text-center mb-6">
                  <div className="text-3xl text-white mb-2">299€</div>
                  <div className="text-gray-400 line-through">349€</div>
                  <Badge className="bg-red-500 text-white">{t("courses.advanced.level")}</Badge>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-red-500 mr-3" />
                    <span className="text-white">{t("courses.advanced.date")}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-red-500 mr-3" />
                    <span className="text-white">{t("courses.advanced.time")}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-red-500 mr-3" />
                    <span className="text-white">{t("courses.advanced.students")}</span>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 mb-4"
                  onClick={() => onNavigateToCheckout('online-avanzado')}
                >
                  {t("courses.advanced.enrollButton")}
                </Button>

                <div className="text-center text-sm text-gray-400 mb-6">
                  {t("courses.advanced.guarantee")}
                </div>

                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg mb-4 text-white">{t("courses.advanced.includes")}</h4>
                  <div className="space-y-2">
                    {[
                      t("courses.advanced.feature1"),
                      t("courses.advanced.feature2"),
                      t("courses.advanced.feature3"),
                      t("courses.advanced.feature4"),
                      t("courses.advanced.feature5"),
                      t("courses.advanced.feature6"),
                      t("courses.advanced.feature7"),
                      t("courses.advanced.feature8")
                    ].map((item, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructor Info */}
                <div className="border-t border-gray-700 pt-6 mt-6">
                  <h4 className="text-lg mb-4 text-white">{t("courses.advanced.instructor")}</h4>
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white text-xl">PC</span>
                    </div>
                    <div>
                      <h5 className="text-white">Pedro Cifuentes</h5>
                      <p className="text-gray-400 text-sm">{t("courses.advanced.instructorTitle")}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">
                    {t("courses.advanced.instructorDescription")}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    onClick={onNavigateToNuestrosCursos}
                  >
                    Conoce más sobre el instructor
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schedule and FAQ Section */}
      <ScheduleAndFAQ
        dates={scheduleData.dates}
        faq={scheduleData.faq}
        onCta={onNavigateToCheckout}
      />

      {/* Bundle Offer Banner */}
      {showBundleOffer && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 shadow-lg"
        >
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Sparkles className="w-6 h-6 text-yellow-200" />
              <div>
                <div className="text-lg">
                  <span className="font-bold">Bundle Completo: Iniciación + Avanzado</span>
                </div>
                <div className="text-sm">
                  <span className="line-through opacity-75">599€</span>
                  <span className="ml-2 text-xl font-bold">499€</span>
                  <span className="ml-2 bg-white text-orange-600 px-2 py-1 rounded text-xs font-bold">
                    ¡AHORRA 100€!
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-100 font-bold"
              >
                CONSEGUIR BUNDLE
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBundleOffer(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}